import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { supabase } from '../lib/supabase'

// Debug logging helper to validate flow during new routes/pages rollout
function logDebug(scope, payload = {}) {
  // Keep lightweight; can be toggled later via env if needed
  console.debug(`[personals:${scope}]`, payload)
}

export const usePersonalsStore = defineStore('personals', () => {
  // State
  const personalAds = ref([])
  const myAds = ref([])
  const myReplies = ref([])
  const inboxReplies = ref([])
  const receivedReplies = ref([])
  const currentAd = ref(null)
  const currentAdReplies = ref([])
  const loading = ref(false)
  const error = ref(null)
  const readReplies = useLocalStorage('personals-read-replies', {})
  const pagination = ref({
    currentPage: 0,
    hasMore: true,
    limit: 20
  })

  // Rate limiting state
  const replyCount = ref(0)
  const lastReplyHour = ref(null)
  const maxRepliesPerHour = 10

  // Computed
  const canReply = computed(() => {
    const currentHour = new Date().getHours()
    if (lastReplyHour.value !== currentHour) {
      return true
    }
    return replyCount.value < maxRepliesPerHour
  })

  const activeAdsCount = computed(() => {
    return myAds.value.filter(ad => ad.is_active).length
  })

  const canCreateAd = computed(() => {
    return activeAdsCount.value < 3
  })

  const unreadInboxCount = computed(() => {
    return inboxReplies.value.filter(r => !readReplies.value[r.id]).length
  })

  // Actions
  async function fetchPersonalAds(search = null, loadMore = false) {
    try {
      loading.value = true
      error.value = null

      const offset = loadMore ? personalAds.value.length : 0
      if (!loadMore) {
        personalAds.value = []
        pagination.value.currentPage = 0
      }

      const { data, error: fetchError } = await supabase.rpc('get_personal_ads', {
        p_category: null, // categories removed
        p_search: search,
        p_limit: pagination.value.limit,
        p_offset: offset
      })

      if (fetchError) throw fetchError

      if (loadMore) {
        personalAds.value.push(...data)
      } else {
        personalAds.value = data
      }

      pagination.value.hasMore = data.length === pagination.value.limit
      pagination.value.currentPage++
    } catch (err) {
      console.error('Error fetching personal ads:', err)
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  async function fetchPersonalAd(id) {
    try {
      loading.value = true
      error.value = null

      // Use direct table select for a single ad
      const { data, error: fetchError } = await supabase
        .from('personal_ads')
        .select('*')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError
      if (!data) throw new Error('Ad not found or no longer available')

      // Enrich with is_mine flag so UI can hide reply button for poster
      try {
        const { data: userRes } = await supabase.auth.getUser()
        const userId = userRes?.user?.id || null
        data.is_mine = userId ? data.poster_id === userId : false
      } catch (_) {
        data.is_mine = false
      }

      currentAd.value = data
    } catch (err) {
      console.error('Error fetching personal ad:', err)
      error.value = err.message
      currentAd.value = null
    } finally {
      loading.value = false
    }
  }

  async function createPersonalAd(adData) {
    try {
      loading.value = true
      error.value = null

      // Check if user can create more ads
      if (!canCreateAd.value) {
        throw new Error('Maximum of 3 active ads allowed')
      }

      const {
        data: { user }
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data: adId, error: createError } = await supabase.rpc('create_personal_ad', {
        p_title: adData.title.trim(),
        p_body: adData.body.trim()
      })

      if (createError) throw createError

      // Get the created ad details
      const { data, error: fetchError } = await supabase
        .from('personal_ads')
        .select('*')
        .eq('id', adId)
        .single()

      if (fetchError) throw fetchError

      // Add to myAds if we have them loaded
      if (myAds.value.length > 0) {
        myAds.value.unshift(data)
      }

      return data
    } catch (err) {
      console.error('Error creating personal ad:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function replyToAd(adId, message) {
    try {
      loading.value = true
      error.value = null

      // Check rate limiting
      if (!canReply.value) {
        throw new Error('Reply limit reached. Try again next hour.')
      }

      const {
        data: { user }
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error: replyError } = await supabase
        .from('personal_replies')
        .insert({
          ad_id: adId,
          responder_id: user.id,
          message: message.trim()
        })
        .select()
        .single()

      if (replyError) throw replyError

      // Update rate limiting
      const currentHour = new Date().getHours()
      if (lastReplyHour.value !== currentHour) {
        replyCount.value = 1
        lastReplyHour.value = currentHour
      } else {
        replyCount.value++
      }

      // Update reply count in current ad if it's loaded and user is not the poster
      if (currentAd.value?.id === adId) {
        currentAd.value.reply_count = (currentAd.value.reply_count || 0) + 1
      }

      // Update reply count in ads list
      const adIndex = personalAds.value.findIndex(ad => ad.id === adId)
      if (adIndex !== -1) {
        personalAds.value[adIndex].reply_count++
      }

      return data
    } catch (err) {
      console.error('Error replying to ad:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchMyAds() {
    try {
      loading.value = true
      error.value = null

      const {
        data: { user }
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error: fetchError } = await supabase
        .from('personal_ads')
        .select('*')
        .eq('poster_id', user.id)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      myAds.value = data
    } catch (err) {
      console.error('Error fetching my ads:', err)
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  async function fetchAdReplies(adId) {
    try {
      loading.value = true
      error.value = null
      logDebug('fetchAdReplies:start', { adId })

      // Use secure function that includes responder profiles for poster
      const { data: repliesData, error: repliesError } = await supabase.rpc('get_ad_replies', {
        p_ad_id: adId
      })

      if (repliesError) {
        // If access denied, user is not the poster - clear replies
        if (repliesError.message?.includes('Access denied')) {
          currentAdReplies.value = []
          logDebug('fetchAdReplies:access_denied', { adId })
          return
        }
        throw repliesError
      }

      currentAdReplies.value = repliesData || []
      logDebug('fetchAdReplies:success', { count: currentAdReplies.value.length })
    } catch (err) {
      console.error('Error fetching ad replies:', err)
      error.value = err.message
      currentAdReplies.value = []
      logDebug('fetchAdReplies:error', { message: err.message })
    } finally {
      loading.value = false
    }
  }

  async function fetchMyReplies() {
    try {
      loading.value = true
      error.value = null
      const {
        data: { user }
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')
      logDebug('fetchMyReplies:start', { userId: user.id })

      const { data, error: fetchError } = await supabase
        .from('personal_replies')
        .select(
          `
          id,
          message,
          created_at,
          personal_ads!inner(
            id,
            title,
            created_at,
            is_active
          )
        `
        )
        .eq('responder_id', user.id)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      myReplies.value = data || []
      logDebug('fetchMyReplies:success', { count: myReplies.value.length })
    } catch (err) {
      console.error('Error fetching my replies:', err)
      error.value = err.message
      logDebug('fetchMyReplies:error', { message: err.message })
    } finally {
      loading.value = false
    }
  }

  async function fetchInboxReplies() {
    try {
      loading.value = true
      error.value = null
      const {
        data: { user }
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')
      logDebug('fetchInboxReplies:start', { userId: user.id })

      const { data, error: fetchError } = await supabase
        .from('personal_replies')
        .select(
          `
          id,
          message,
          created_at,
          ad_id,
          personal_ads!inner(
            id,
            title,
            created_at,
            is_active,
            poster_id
          )
        `
        )
        .eq('personal_ads.poster_id', user.id)
        .neq('responder_id', user.id)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      inboxReplies.value = data || []
      logDebug('fetchInboxReplies:success', { count: inboxReplies.value.length })
    } catch (err) {
      console.error('Error fetching inbox replies:', err)
      error.value = err.message
      inboxReplies.value = []
      logDebug('fetchInboxReplies:error', { message: err.message })
    } finally {
      loading.value = false
    }
  }

  async function fetchReceivedReplies() {
    try {
      loading.value = true
      error.value = null
      const {
        data: { user }
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')
      logDebug('fetchReceivedReplies:start', { userId: user.id })

      const { data, error: fetchError } = await supabase
        .from('personal_replies')
        .select(
          `
          id,
          message,
          created_at,
          ad_id,
          personal_ads!inner(
            id,
            title,
            created_at,
            is_active,
            poster_id
          )
        `
        )
        .eq('personal_ads.poster_id', user.id)
        .neq('responder_id', user.id)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      receivedReplies.value = data || []
      logDebug('fetchReceivedReplies:success', { count: receivedReplies.value.length })
    } catch (err) {
      console.error('Error fetching received replies:', err)
      error.value = err.message
      receivedReplies.value = []
      logDebug('fetchReceivedReplies:error', { message: err.message })
    } finally {
      loading.value = false
    }
  }

  function markReplyRead(id) {
    readReplies.value[id] = true
  }

  function markAllInboxRepliesRead() {
    inboxReplies.value.forEach(r => {
      readReplies.value[r.id] = true
    })
  }

  function isReplyUnread(id) {
    return !readReplies.value[id]
  }

  async function deleteAd(adId) {
    try {
      loading.value = true
      error.value = null

      const { data, error: deleteError } = await supabase.rpc('delete_personal_ad', {
        p_ad_id: adId
      })

      if (deleteError) throw deleteError

      // Update myAds - mark as inactive instead of removing
      const myAdIndex = myAds.value.findIndex(ad => ad.id === adId)
      if (myAdIndex !== -1) {
        myAds.value[myAdIndex].is_active = false
        myAds.value[myAdIndex].updated_at = new Date().toISOString()
      }

      // Remove from personalAds since it's no longer active
      personalAds.value = personalAds.value.filter(ad => ad.id !== adId)
    } catch (err) {
      console.error('Error deleting ad:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updatePersonalAd(adId, updates) {
    try {
      loading.value = true
      error.value = null

      const { data, error: updateError } = await supabase.rpc('update_personal_ad', {
        p_ad_id: adId,
        p_title: updates.title.trim(),
        p_body: updates.body.trim()
      })

      if (updateError) throw updateError

      // Fetch the updated ad to get all fields
      const { data: updatedAd, error: fetchError } = await supabase
        .from('personal_ads')
        .select('*')
        .eq('id', adId)
        .single()

      if (fetchError) throw fetchError

      // Update in myAds
      const myAdIndex = myAds.value.findIndex(ad => ad.id === adId)
      if (myAdIndex !== -1) {
        myAds.value[myAdIndex] = updatedAd
      }

      // Update in currentAd if it's loaded
      if (currentAd.value?.id === adId) {
        currentAd.value = updatedAd
      }

      return updatedAd
    } catch (err) {
      console.error('Error updating ad:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchPersonalAdForEdit(adId) {
    try {
      loading.value = true
      error.value = null

      const { data, error: fetchError } = await supabase.rpc('get_personal_ad_for_edit', {
        p_ad_id: adId
      })

      if (fetchError) throw fetchError
      if (!data || data.length === 0)
        throw new Error('Ad not found or you do not have permission to edit it')

      return data[0]
    } catch (err) {
      console.error('Error fetching ad for edit:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // Reset functions
  function clearCurrentAd() {
    currentAd.value = null
    currentAdReplies.value = []
  }

  function clearError() {
    error.value = null
  }

  function resetPagination() {
    pagination.value = {
      currentPage: 0,
      hasMore: true,
      limit: 20
    }
  }

  return {
    // State
    personalAds,
    myAds,
    myReplies,
    inboxReplies,
    receivedReplies,
    currentAd,
    currentAdReplies,
    loading,
    error,
    pagination,
    readReplies,

    // Computed
    canReply,
    activeAdsCount,
    canCreateAd,
    unreadInboxCount,

    // Actions
    fetchPersonalAds,
    fetchPersonalAd,
    createPersonalAd,
    replyToAd,
    fetchMyAds,
    fetchAdReplies,
    fetchMyReplies,
    fetchInboxReplies,
    fetchReceivedReplies,
    deleteAd,
    updatePersonalAd,
    fetchPersonalAdForEdit,
    clearCurrentAd,
    clearError,
    resetPagination,
    markReplyRead,
    markAllInboxRepliesRead,
    isReplyUnread
  }
})
